/** Five colours to choose between, and the default is what was always there.
 *
 *  A swatch each rather than a dropdown: the thing being chosen IS a colour, and a
 *  menu that has to be opened to see the options is the wrong control for that. It
 *  sits at the foot of the rail on its own row under the identity block - the two
 *  things about you rather than about the work.
 *
 *  On its own row because five 20px swatches and the avatar do not both fit 212px:
 *  side by side they truncated the user's name to one letter. A sixth swatch will not
 *  fit even alone, and is the point at which this should become a popover.
 */
import { useState } from 'react'
import { THEMES, applyTheme, readTheme, type Theme } from '@/lib/theme'

/** The ground and the primary of each theme, for the swatch. Duplicated from the CSS
 *  deliberately: a variable set on `<html>` for a theme that is not currently applied
 *  cannot be read, and five swatches all showing the CURRENT theme's colours would be
 *  no help at all. `theme.test.ts` checks the two lists cannot drift apart. */
const SWATCH: Record<Theme, [string, string]> = {
  default: ['#FAF9F6', '#1A3A5C'],
  slate: ['#F4F6F8', '#114E5E'],
  ledger: ['#F5F2EB', '#204236'],
  midnight: ['#1E2430', '#7DB3E8'],
  graphite: ['#2B2926', '#84AC9E'],
}

export function ThemePicker() {
  const [theme, setTheme] = useState<Theme>(() => readTheme())
  const pick = (t: Theme) => { applyTheme(t); setTheme(t) }
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Colour theme">
      {THEMES.map(([value, label, hint]) => {
        const [ground, accent] = SWATCH[value]
        const on = theme === value
        return (
          <button key={value} type="button" role="radio" aria-checked={on}
                  onClick={() => pick(value)}
                  title={`${label} — ${hint}`}
                  aria-label={`${label} theme`}
                  className={`h-5 w-5 rounded-full border grid place-items-center
                              transition-transform hover:scale-110 ${
                    on ? 'border-primary ring-1 ring-primary' : 'border-rule'}`}
                  style={{ background: ground }}>
            <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
          </button>
        )
      })}
    </div>
  )
}
